// Filter-Syntax der Suche (Phase 2.B), gemeinsam für API (SQL) und Web (Chips):
//   von: an: betreff: postfach: zugewiesen: schule: rolle: ist: hat: nach: vor:  "genaue Phrase"  -ausschluss
export type SearchFilters = {
  von?: string;
  an?: string;
  betreff?: string;
  postfach?: string;
  zugewiesen?: string;
  schule?: string;
  rolle?: string;
  ist?: 'offen' | 'erledigt' | 'wartet' | 'spaeter' | 'info' | 'junk' | 'alle';
  hat?: 'anhang' | 'pdf';
  nach?: string; // YYYY-MM-DD
  vor?: string;
};
export type ParsedQuery = {
  terms: string[];
  phrases: string[];
  exclude: string[];
  filters: SearchFilters;
  raw: string;
};

const KEYS = [
  'von',
  'an',
  'betreff',
  'postfach',
  'zugewiesen',
  'schule',
  'rolle',
  'ist',
  'hat',
  'nach',
  'vor',
] as const;
const IST = {
  offen: 'offen',
  erledigt: 'erledigt',
  wartet: 'wartet',
  später: 'spaeter',
  spaeter: 'spaeter',
  info: 'info',
  junk: 'junk',
  alle: 'alle',
} as const;

export function parseQuery(q: string): ParsedQuery {
  const out: ParsedQuery = { terms: [], phrases: [], exclude: [], filters: {}, raw: q };
  // Tokens: key:"wert mit leerzeichen" | key:wert | "phrase" | -wort | wort
  const re = /(-?)(?:([a-zäöü]+):)?(?:"([^"]*)"|„([^“]*)“|(\S+))/gi;
  for (const m of q.matchAll(re)) {
    const neg = m[1] === '-';
    const key = m[2]?.toLowerCase();
    const val = (m[3] ?? m[4] ?? m[5] ?? '').trim();
    if (!val && !key) continue;
    if (key && (KEYS as readonly string[]).includes(key)) {
      const k = key as (typeof KEYS)[number];
      if (k === 'ist') {
        const v = IST[val.toLowerCase() as keyof typeof IST];
        if (v) out.filters.ist = v;
      } else if (k === 'hat') {
        if (val.toLowerCase() === 'anhang' || val.toLowerCase() === 'pdf')
          out.filters.hat = val.toLowerCase() as 'anhang' | 'pdf';
      } else if (k === 'nach' || k === 'vor') {
        const d = normDate(val);
        if (d) out.filters[k] = d;
      } else out.filters[k] = val;
      continue;
    }
    const wort = key ? `${key}:${val}` : val;
    if (neg) out.exclude.push(wort);
    else if (m[3] != null || m[4] != null) out.phrases.push(wort);
    else out.terms.push(wort);
  }
  return out;
}

// 2026-09-01, 1.9.2026, 01.09.26 → YYYY-MM-DD
function normDate(s: string): string | null {
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (iso) return `${iso[1]}-${iso[2]!.padStart(2, '0')}-${iso[3]!.padStart(2, '0')}`;
  const de = /^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/.exec(s);
  if (de) {
    const y = de[3]!.length === 2 ? '20' + de[3] : de[3];
    return `${y}-${de[2]!.padStart(2, '0')}-${de[1]!.padStart(2, '0')}`;
  }
  return null;
}

export const FILTER_LABELS: Record<keyof SearchFilters, string> = {
  von: 'von',
  an: 'an',
  betreff: 'Betreff',
  postfach: 'Postfach',
  zugewiesen: 'zugewiesen',
  schule: 'Schule',
  rolle: 'Rolle',
  ist: 'ist',
  hat: 'hat',
  nach: 'nach',
  vor: 'vor',
};

// Freitext der Suche ohne die Filter (für die Volltextsuche)
export function freeText(p: ParsedQuery): string {
  return [...p.terms, ...p.phrases.map((x) => `"${x}"`)].join(' ');
}

// Eine Filterangabe wieder in die Zeile zurückschreiben (Chips → Suchzeile)
export function withFilter(q: string, key: keyof SearchFilters, value: string | null): string {
  const p = parseQuery(q);
  const rest = [...p.terms, ...p.phrases.map((x) => `"${x}"`), ...p.exclude.map((x) => '-' + x)];
  const f: SearchFilters = { ...p.filters, [key]: value ?? undefined };
  const parts = KEYS.filter((k) => f[k]).map(
    (k) => `${k}:${/\s/.test(String(f[k])) ? `"${f[k]}"` : f[k]}`,
  );
  return [...parts, ...rest].join(' ').trim();
}
